import React, { createContext, useState, useEffect, useMemo } from 'react';
import api, { setAuthToken, VIEW_AS_TOKEN_KEY, VIEW_AS_USER_KEY, clearViewAs } from '../services/api';
import i18n from '../i18n';
import { API_BASE } from '../utils/apiBase';

function applyLanguage(lang) {
  if (lang && (lang === 'fr' || lang === 'en')) {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18n_language', lang);
  }
}

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => { const savedToken = localStorage.getItem('authToken'); const savedUser = localStorage.getItem('authUser'); return savedToken && savedUser ? { token: savedToken, user: JSON.parse(savedUser) } : { token: null, user: null }; });

  // 'admin' | 'pb' | null — null means mode selector not yet shown.
  // sessionStorage so it clears on tab/browser close (right default for a role switch).
  const [mode, setModeState] = useState(() => sessionStorage.getItem('authMode') || null);

  // Active tenant context — set when a super admin enters a workspace.
  // sessionStorage: persists across page navigation, clears on tab close.
  const [activeTenantId,   setActiveTenantIdState]   = useState(() => sessionStorage.getItem('activeTenantId')   || null);
  const [activeTenantName, setActiveTenantNameState] = useState(() => sessionStorage.getItem('activeTenantName') || null);

  // Active firm/brokerage context — set when a super admin enters a specific firm.
  // localStorage: persists across sessions; cleared on logout.
  const [activeFirmId,   setActiveFirmIdState]   = useState(() => localStorage.getItem('active_firm_id')   || null);
  const [activeFirmName, setActiveFirmNameState] = useState(() => localStorage.getItem('active_firm_name') || null);

  useEffect(() => {
    const savedToken = localStorage.getItem('authToken');
    const savedUser  = localStorage.getItem('authUser');
    if (savedToken && savedUser) {
      const user = JSON.parse(savedUser);
      setAuth({ token: savedToken, user });
      if (user?.language) applyLanguage(user.language);
    }
  }, []);

  const selectMode = (m) => {
    sessionStorage.setItem('authMode', m);
    setModeState(m);
  };

  const setActiveFirm = (id, name) => {
    if (id) {
      localStorage.setItem('active_firm_id',   String(id));
      localStorage.setItem('active_firm_name', name || '');
    } else {
      localStorage.removeItem('active_firm_id');
      localStorage.removeItem('active_firm_name');
    }
    setActiveFirmIdState(id ? String(id) : null);
    setActiveFirmNameState(name || null);
  };

  const setActiveTenant = (id, name) => {
    if (id) {
      sessionStorage.setItem('activeTenantId',   String(id));
      sessionStorage.setItem('activeTenantName', name || '');
    } else {
      sessionStorage.removeItem('activeTenantId');
      sessionStorage.removeItem('activeTenantName');
    }
    setActiveTenantIdState(id ? String(id) : null);
    setActiveTenantNameState(name || null);
  };

  const login = async (token, user, refreshToken) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('authUser', JSON.stringify(user));
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    setAuthToken(token); // sync axios header immediately — before any navigation/render
    if (user?.language) applyLanguage(user.language);
    // Clear mode so super admins see the selector again on each new login session.
    sessionStorage.removeItem('authMode');
    setModeState(null);

    // Checked once, right here at login -- not on every subsequent page
    // load -- matching "every existing user at their next login" exactly.
    // A version bump in config/legalDocument.js only affects an existing
    // session the next time it actually logs in again, not mid-session.
    let needsLegalAcceptance = false;
    try {
      const res = await api.get('/legal-documents/acceptance-status');
      needsLegalAcceptance = !!res.data?.data?.needsAcceptance;
    } catch (_) {
      // Don't block login over a failed check (e.g. a network blip) --
      // worst case the user is asked again on their next login instead.
    }

    const mergedUser = { ...user, needsLegalAcceptance };
    localStorage.setItem('authUser', JSON.stringify(mergedUser));
    setAuth({ token, user: mergedUser });
  };

  const logout = async () => {
    // Signing out during View As also ends it on the server (audited).
    if (sessionStorage.getItem(VIEW_AS_TOKEN_KEY)) {
      clearViewAs();
      try { await api.post('/admin/view-as/stop'); } catch (_) {}
    }
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      try {
        await fetch(
          `${API_BASE}/auth/logout`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
          }
        );
      } catch (_) {}
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('active_firm_id');
    localStorage.removeItem('active_firm_name');
    sessionStorage.removeItem('authMode');
    sessionStorage.removeItem('activeTenantId');
    sessionStorage.removeItem('activeTenantName');
    clearViewAs();
    setViewAs(null);
    setAuthToken(null); // clear axios Authorization header immediately
    setModeState(null);
    setActiveTenantIdState(null);
    setActiveTenantNameState(null);
    setActiveFirmIdState(null);
    setActiveFirmNameState(null);
    setAuth({ token: null, user: null });
  };

  const updateToken = (token, user) => {
    localStorage.setItem('authToken', token);
    if (user) localStorage.setItem('authUser', JSON.stringify(user));
    setAuth(prev => ({ token, user: user || prev.user }));
  };

  const updateUser = (userPatch) => {
    setAuth(prev => {
      const merged = { ...prev.user, ...userPatch };
      localStorage.setItem('authUser', JSON.stringify(merged));
      return { ...prev, user: merged };
    });
  };

  // ── View As (batch G, G2) ──────────────────────────────────────────────────
  // A super admin views the app as one chosen user. The backend issues a
  // 30-minute read-only token for that user (POST /admin/view-as); api.js
  // sends it on every request while it is set, so data loads exactly as that
  // user sees it, and the server refuses every write. sessionStorage: it
  // survives a reload and clears when the tab closes. While active, `auth`
  // is the viewed user; `realUser` is the super admin.
  const [viewAs, setViewAs] = useState(() => {
    const token = sessionStorage.getItem(VIEW_AS_TOKEN_KEY);
    const user  = sessionStorage.getItem(VIEW_AS_USER_KEY);
    try { return token && user ? { token, user: JSON.parse(user) } : null; } catch (_) { return null; }
  });

  const startViewAs = async (userId) => {
    const r = await api.post('/admin/view-as', { userId });
    const { token, user } = r.data;
    setActiveFirm(null, null);
    setActiveTenant(null, null);
    sessionStorage.setItem(VIEW_AS_TOKEN_KEY, token);
    sessionStorage.setItem(VIEW_AS_USER_KEY, JSON.stringify(user));
    setViewAs({ token, user });
    selectMode('pb');
    return user;
  };

  const stopViewAs = async () => {
    clearViewAs();
    setViewAs(null);
    selectMode('admin');
    try { await api.post('/admin/view-as/stop'); } catch (_) {}
  };

  const effectiveUser = viewAs ? viewAs.user : auth.user;
  const visibleAuth = useMemo(
    () => (viewAs ? { token: viewAs.token, user: viewAs.user } : auth),
    [viewAs, auth]
  );

  return (
    <AuthContext.Provider value={{
      auth: visibleAuth, realUser: auth.user, login, logout, updateToken, updateUser,
      mode, selectMode,
      activeTenantId, activeTenantName, setActiveTenant,
      activeFirmId,   activeFirmName,   setActiveFirm,
      viewAs, startViewAs, stopViewAs,
      effectiveUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
